import React, { FC, ReactElement, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import { LinkButton } from "@natera/form";
import { useErrorController } from "@natera/platform/lib/hooks";
import { routes } from "@app/routing";
import { isValidLanguageValue, useQuery } from "@app/utils";
import { EnrollFactorResponse } from "@app/service/user";
import {
  ErrorProvider,
  ConfigContext,
  IntlContext,
  Language,
  MfaErrorProvider,
  UserContext,
  useSignInContext,
  LoginMode,
} from "@app/provider";
import {
  AuthTitle,
  LoginModeSwitch,
  NeedHelpLink,
  ChooseSignInTwoFactorMethod,
  SignInWithAuthenticationProviders,
  ForgotEmail,
} from "@app/components";
import SpinnerView from "@app/components/spinnerView";
import { LoginForm } from "@app/components/forms";
import VerifyModal from "@app/components/verifyModal";
import { useTokenSourceFromQuery } from "@app/hooks";

import "./signIn.scss";

const messages = defineMessages({
  signInEmail: {
    id: "signInEmail",
    defaultMessage: "Email Address",
  },
  signInPasswordLabel: {
    id: "signInPasswordLabel",
    defaultMessage: "Password",
  },
  signInEmptyFactor: {
    id: "signInEmptyFactor",
    defaultMessage: "We're sorry. Something went wrong.",
  },
  signInForgotPasswordButton: {
    id: "signInForgotPasswordButton",
    defaultMessage: "Forgot password?",
  },
  signInNeedAccount: {
    id: "signInNeedAccount",
    defaultMessage: "Already have an account?",
  },
  signInTryAgain: {
    id: "signInTryAgain",
    defaultMessage:
      "Please try again. If you have not activated your account, please check your email or request a new activation link.",
  },
  userStatusInactive: {
    id: "userStatusInactive",
    defaultMessage: "Your account has not been activated.",
  },
  highlightedTooltipDescriptionForm: {
    id: "highlightedTooltipDescriptionForm",
    defaultMessage: "You previously logged in with your Natera account.",
  },
});

export const handleFactors = (
  factors: EnrollFactorResponse[],
  setFactor: React.Dispatch<
    React.SetStateAction<EnrollFactorResponse | undefined>
  >,
  setShowFactorSelection: React.Dispatch<React.SetStateAction<boolean>>
): void => {
  if (factors?.length === 1) {
    // It is assumed that if the user has only 1 factor, then it is an EMAIL
    const selectedFactor = factors[0];
    setFactor(selectedFactor);
  } else {
    setShowFactorSelection(true);
  }
};

const SignInContent: FC = () => {
  const intl = useIntl();
  const query = useQuery();
  const { isLoading } = useContext(UserContext);
  const { config } = useContext(ConfigContext);
  const { changeLanguage, currentLanguage } = useContext(IntlContext);
  const errorController = useErrorController();

  const signInContext = useSignInContext();
  const {
    loginMode,
    switchLoginMode,
    handlePasswordSubmit,
    isLoginPending,
    handleOtpRequest,
    isOtpRequestPending,
    otpErrorMessage,
    factors,
    factor,
    setFactor,
    showFactorSelection,
    setShowFactorSelection,
  } = signInContext;

  const { token, tokenSource, lang } = useTokenSourceFromQuery();

  const emailVerified = query.get("email_verified");

  const isOtpVerificationEnabled = config.otpVerification?.enabled;

  useEffect(() => {
    if (lang && currentLanguage !== lang && isValidLanguageValue(lang)) {
      changeLanguage(lang as Language);
    }
  }, []);

  const handleSelectFactor = (factor?: EnrollFactorResponse) => {
    setFactor(factor);
    setShowFactorSelection(false);
  };

  const handleGoBack = () => {
    setFactor(undefined);
    setShowFactorSelection((factors?.length ?? 0) > 1);
  };

  const authenticationProviders: ReactElement = (
    <SignInWithAuthenticationProviders
      invite={token}
      tokenSource={tokenSource}
      isSignInFlow
    />
  );

  const renderPasswordForm = () => (
    <section className="form__container">
      <ErrorProvider controller={errorController}>
        <LoginForm
          setValidationError={errorController.setValidationError}
          clearValidationError={errorController.clearValidationError}
          clearErrors={errorController.clearErrors}
          isLoading={isLoginPending}
          onSubmit={handlePasswordSubmit}
          prefilledEmail={emailVerified ?? undefined}
        />
      </ErrorProvider>
    </section>
  );

  const oneTimeCodeFormConfig = {
    onSubmit: handleOtpRequest,
    isLoading: isOtpRequestPending,
    errorMessage: otpErrorMessage,
    prefilledEmail: emailVerified ?? undefined,
    usePlusSignInEmailAddress: config.test.usePlusSignInEmailAddress.enabled,
    contactUsUrl: config.links.CONTACT_US,
  };

  const forgotPassword: ReactElement = (
    <section className="forgot-password__container">
      <LinkButton
        to={routes.resetPassword}
        renderLink={(props) => (
          <Link
            {...props}
            aria-label={intl.formatMessage(messages.signInForgotPasswordButton)}
          />
        )}
        outlined
        tabIndex={-1}
        aria-hidden={true}
      >
        {intl.formatMessage(messages.signInForgotPasswordButton)}
      </LinkButton>
    </section>
  );

  const showMfaFactorSelection = Boolean(
    factors?.length && showFactorSelection
  );
  const showVerifyStep = Boolean(factor);

  if (showMfaFactorSelection) {
    return (
      <MfaErrorProvider>
        <ChooseSignInTwoFactorMethod
          factors={factors!}
          onSubmitFactor={handleSelectFactor}
        />
      </MfaErrorProvider>
    );
  }

  if (showVerifyStep) {
    return (
      <MfaErrorProvider>
        <VerifyModal
          factor={factor!}
          setFactor={setFactor}
          goBack={handleGoBack}
        />
      </MfaErrorProvider>
    );
  }

  return (
    <MfaErrorProvider>
      <article className="sign-in__container">
        <SpinnerView
          isLoading={isLoading || isLoginPending || isOtpRequestPending}
        />
        <AuthTitle />
        {authenticationProviders}
        <ErrorProvider controller={errorController}>
          <LoginModeSwitch
            loginMode={loginMode}
            onSwitchMode={switchLoginMode}
            oneTimeCodeFormConfig={oneTimeCodeFormConfig}
            switchButtonClassName="sign-in__switch-button"
            isOtpVerificationEnabled={isOtpVerificationEnabled}
          >
            <>
              {renderPasswordForm()}
              {forgotPassword}
            </>
          </LoginModeSwitch>
        </ErrorProvider>
        {loginMode === LoginMode.OTP && (
          <ForgotEmail url={oneTimeCodeFormConfig.contactUsUrl} />
        )}
        <NeedHelpLink />
      </article>
    </MfaErrorProvider>
  );
};

const SignIn: FC = () => <SignInContent />;

export default SignIn;
